import type { ReactNode } from "react";
import { chapterMeta } from "@/engine/chapters";
import { AccentContext } from "./accent";
import { Badge } from "./Badge";

interface SlideShellProps {
  chapter: number;
  /** Overrides the default "0N · Chapter Title" kicker badge text. */
  kicker?: string;
  title?: ReactNode;
  align?: "left" | "center";
  children: ReactNode;
}

/**
 * Standard slide scaffold: kicker badge, display title, centered content area.
 * Mandatory for non-title slides — keeps all chapters visually consistent.
 */
export function SlideShell({
  chapter,
  kicker,
  title,
  align = "left",
  children,
}: SlideShellProps) {
  const meta = chapterMeta(chapter);
  return (
    <AccentContext.Provider value={meta.accent}>
      <div
        className={`flex h-full w-full flex-col px-24 py-16 ${
          align === "center" ? "items-center text-center" : ""
        }`}
      >
        {(kicker !== "" || title) && (
          <header className={`mb-10 shrink-0 ${align === "center" ? "flex flex-col items-center" : ""}`}>
            {kicker !== "" && (
              <Badge className="mb-5">
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(90deg, ${meta.accent[0]}, ${meta.accent[1]})`,
                  }}
                >
                  {kicker ?? `${String(chapter).padStart(2, "0")} · ${meta.title}`}
                </span>
              </Badge>
            )}
            {title && (
              <h1 className="font-display text-6xl font-semibold tracking-tight text-white">
                {title}
              </h1>
            )}
          </header>
        )}
        <div className="flex w-full min-h-0 flex-1 flex-col justify-center">
          {children}
        </div>
      </div>
    </AccentContext.Provider>
  );
}
